import { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import * as XLSX from "xlsx"; // ⬅️ Add XLSX for Excel export
import api from "../../api/api";

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  // Export filters
  const [tourSearch, setTourSearch] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    fetchBookings();
  }, [filter]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/bookings/admin/all${filter !== "all" ? `?status=${filter}` : ""}`
      );
      setBookings(res.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (bookingId, status) => {
    try {
      await api.put(`/bookings/${bookingId}/admin-status`, { status });
      setBookings(
        bookings.map((b) => (b.id === bookingId ? { ...b, status } : b))
      );
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleDelete = async (bookingId) => {
    if (window.confirm("Are you sure you want to delete this booking?")) {
      try {
        await api.delete(`/bookings/${bookingId}`);
        setBookings(bookings.filter((b) => b.id !== bookingId));
      } catch (error) {
        console.error("Error deleting booking:", error);
      }
    }
  };

  // Apply export filtering
  const getFilteredData = () => {
    return bookings.filter((b) => {
      const price = Number(b.total_price || 0);
      const created = new Date(b.created_at);

      const matchTour =
        tourSearch.trim() === "" ||
        b.tour_title.toLowerCase().includes(tourSearch.toLowerCase());

      const matchMin = minPrice === "" || price >= Number(minPrice);
      const matchMax = maxPrice === "" || price <= Number(maxPrice);

      const matchFrom =
        dateFrom === "" || created >= new Date(dateFrom + "T00:00:00");

      const matchTo =
        dateTo === "" || created <= new Date(dateTo + "T23:59:59");

      return matchTour && matchMin && matchMax && matchFrom && matchTo;
    });
  };
 const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Export to Excel
  const exportExcel = () => {
    const filtered = getFilteredData();

    if (filtered.length === 0) {
      alert("No data available for the selected filters.");
      return;
    }

    const data = filtered.map((b) => ({
      ID: b.id,
      User: b.user_name,
      Email: b.email,
      Tour: b.tour_title,
      Participants: b.participants,
      Price: b.total_price,
      Status: b.status,
      Date: new Date(b.created_at).toLocaleDateString(),
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Bookings");
    XLSX.writeFile(workbook, "Bookings_Report.xlsx");
  };

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-700 border-yellow-300",
    confirmed: "bg-blue-100 text-blue-700 border-blue-300",
    completed: "bg-green-100 text-green-700 border-green-300",
    cancelled: "bg-red-100 text-red-700 border-red-300",
  };

  return (
    <div className="">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
          Manage Bookings
        </h2>

        {/* Status Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          {["all", "pending", "confirmed", "completed"].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`
                px-4 py-2 rounded-lg border text-sm font-medium transition-all shadow-sm
                ${
                  filter === status
                    ? "bg-blue-600 text-white border-blue-600 shadow-md scale-[1.03]"
                    : "bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700"
                }
              `}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Export Filters */}
      <div className="bg-white dark:bg-slate-900 border p-4 rounded-xl shadow mb-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Export Filters
        </h3>

        <div className="grid md:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Search by tour title"
            className="px-3 py-2 rounded-lg border dark:bg-slate-800 w-full"
            value={tourSearch}
            onChange={(e) => setTourSearch(e.target.value)}
          />

          <input
            type="number"
            placeholder="Min Price"
            className="px-3 py-2 rounded-lg border dark:bg-slate-800 w-full"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
          />

          <input
            type="number"
            placeholder="Max Price"
            className="px-3 py-2 rounded-lg border dark:bg-slate-800 w-full"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
          />

          <input
            type="date"
            className="px-3 py-2 rounded-lg border dark:bg-slate-800 w-full"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />

          <input
            type="date"
            className="px-3 py-2 rounded-lg border dark:bg-slate-800 w-full"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        <button
          onClick={exportExcel}
          className="px-6 py-3 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition text-sm font-medium"
        >
          Export to Excel
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-20 text-gray-600 dark:text-gray-300 text-lg">
          Loading bookings...
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 dark:bg-slate-800">
              <tr>
                {[
                  "ID",
                  "User",
                  "Tour",
                  "Participants",
                  "Price",
                 " Tour Date",
                  "Status",
                  "Booking Date",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 font-semibold text-gray-900 dark:text-white text-sm"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
              {bookings.map((booking) => (
                <tr
                  key={booking.id}
                  className="hover:bg-gray-50 dark:hover:bg-slate-800 transition"
                >
                  <td className="py-4 font-mono text-gray-700 dark:text-gray-300">
                    #{booking.id}
                  </td>

                  <td className="px-6 py-4">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {booking.user_name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {booking.email}
                    </p>
                  </td>

                  <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                    {booking.tour_title}
                  </td>

                  <td className="px-6 py-4 text-center text-gray-700 dark:text-gray-300">
                    {booking.participants}
                  </td>

                  <td className="px-6 py-4 font-semibold text-gray-900 dark:text-white">
                    {Number(booking.total_price || 0).toFixed(2)}$
                  </td>
 <td className="px-6 py-4 font-semibold text-gray-900 dark:text-white">
{formatDate(booking.tour_date)}
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={booking.status}
                      onChange={(e) =>
                        handleStatusChange(booking.id, e.target.value)
                      }
                      className={`px-3 py-2 rounded-lg text-sm border ${
                        statusColors[booking.status]
                      } dark:bg-slate-800 dark:text-gray-300 dark:border-slate-600`}
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>

                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                   {formatDate(booking.created_at)} 
                  </td>

                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleDelete(booking.id)}
                      className="p-2 rounded-lg text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 transition"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
